/**
 * Builds `Authorization: <appCheckToken> [userToken]` for the Apollo transport.
 *
 * Imports no Firebase or Apollo by design: the apps are on different Apollo 3.x
 * versions and different Firebase SDKs, so injected getters are what let one
 * implementation serve both. Those SDKs also fail differently — RNFirebase
 * resolves null-ish, the web SDK throws — hence `safeGet`.
 */

/** Which token acquisition failed — lets callers route telemetry per stage. */
export type FirebaseTokenStage = 'appCheck' | 'user'

/** Resolves a Firebase token, or `undefined` when none is available. */
export type FirebaseTokenGetter = () => Promise<string | undefined>

export interface BuildFirebaseAuthorizationHeaderOptions {
  getAppCheckToken: FirebaseTokenGetter
  /** Returns `undefined` when signed out. */
  getUserToken: FirebaseTokenGetter
  /**
   * Substituted when App Check fails or yields nothing.
   *
   * Legacy CWA compatibility only — do not use for new callers (CBP-3932). The
   * Esus authorizer rejects CWA's sentinel with 401 just like any other invalid
   * token, so a fallback does not keep requests working during an App Check
   * outage; it only makes the failure greppable.
   */
  appCheckFallbackToken?: string
  /** Called once per failing stage. Receives the error only, never a token. */
  onError?: (stage: FirebaseTokenStage, error: unknown) => void
}

// Any whitespace breaks the positional format; CR/LF would also split the header.
const containsWhitespace = (value: string) => /\s/.test(value)

const safeGet = async (
  get: FirebaseTokenGetter,
  stage: FirebaseTokenStage,
  onError?: (stage: FirebaseTokenStage, error: unknown) => void
): Promise<string | undefined> => {
  try {
    const token = await get()
    return token && !containsWhitespace(token) ? token : undefined
  } catch (error) {
    onError?.(stage, error)
    return undefined
  }
}

/**
 * Returns the header value, or `undefined` when no App Check token is available
 * and no fallback was supplied. Getters run concurrently.
 */
export async function buildFirebaseAuthorizationHeader({
  getAppCheckToken,
  getUserToken,
  appCheckFallbackToken,
  onError,
}: BuildFirebaseAuthorizationHeaderOptions): Promise<string | undefined> {
  const [resolvedAppCheckToken, userToken] = await Promise.all([
    safeGet(getAppCheckToken, 'appCheck', onError),
    safeGet(getUserToken, 'user', onError),
  ])

  const appCheckToken = resolvedAppCheckToken ?? appCheckFallbackToken

  if (!appCheckToken) return undefined
  return userToken ? `${appCheckToken} ${userToken}` : appCheckToken
}
